/**
 * INT-0027 (scheduler cold start): venue availability in one dialog.
 * Days of week + a time window + a date range → ordinary TimeSlots for the
 * chosen competition, and every time-less game of that competition (the
 * drafted schedule from sign-up) gets a real start time inside them. The
 * preview counts come from the same shared core the API runs, so what the
 * organizer sees is what gets created.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/clerkShim';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useTimeSlotStore from '@store/timeSlotStore';
import useLocationStore from '@store/locationStore';
import helpers from '@/utils/helpers';
import { getSportLabel } from '@staty/shared/utils/sportLabels';
import {
  buildQuickSlots, planGamePlacement, defaultQuickAvailability, formatDateKey,
  type PlaceableGame, type QuickAvailabilityInput,
} from '@staty/shared/utils/quickAvailability';

export interface WizardCompetition {
  kind: 'league' | 'tournament';
  id: string;
  name: string;
}

export interface QuickAvailabilityResult {
  slotsCreated: number;
  slotsSkipped: number;
  gamesPlaced: number;
  gamesUnplaced: number;
  lastGameDate: string | null;
  location: { _id: string; name: string };
  competition: { _id: string; name: string; kind: 'league' | 'tournament' };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: { _id?: string; name?: string; sports?: string[] } | null | undefined;
  /** Fixed competition (schedulers) or null to show a picker over `competitions`. */
  competition?: WizardCompetition | null;
  competitions?: WizardCompetition[];
  /** Time-less games of the competition in context (dates as YYYY-MM-DD). */
  timelessGames?: PlaceableGame[];
  gameDurationInMinutes?: number | null;
  onDone?: (result: QuickAvailabilityResult) => void;
}

const DAYS = [
  { n: 0, short: 'Sun' }, { n: 1, short: 'Mon' }, { n: 2, short: 'Tue' }, { n: 3, short: 'Wed' },
  { n: 4, short: 'Thu' }, { n: 5, short: 'Fri' }, { n: 6, short: 'Sat' },
];

const NEW_VENUE = '__new__';

export default function QuickAvailabilityWizard({
  open, onOpenChange, organization, competition = null, competitions = [], timelessGames = [], gameDurationInMinutes, onDone,
}: Props) {
  const { getToken } = useAuth();
  const quickAvailability = useTimeSlotStore((s: any) => s.quickAvailability(getToken));
  const { locations, getLocationsByLeagueName } = useLocationStore((s: any) => ({
    locations: s.locations, getLocationsByLeagueName: s.getLocationsByLeagueName,
  }));
  const orgId = helpers.getObjId(organization) || '';
  const orgSlug = organization?.name || '';
  const venuesLabel = getSportLabel(organization?.sports?.[0], 'venues');
  const venueSingular = venuesLabel.replace(/s$/, '');

  const [competitionId, setCompetitionId] = useState<string>('');
  const [locationId, setLocationId] = useState<string>('');
  const [newVenueName, setNewVenueName] = useState<string>('');
  const [form, setForm] = useState<QuickAvailabilityInput>(() =>
    defaultQuickAvailability(timelessGames, { today: formatDateKey(new Date()), gameDurationInMinutes }));
  const [duration, setDuration] = useState<string>(String(form.gameDurationInMinutes));
  const [saving, setSaving] = useState(false);
  // Until the organizer touches a field, the defaults follow the data: the
  // drafted games (and the division duration) often arrive AFTER the dialog
  // mounts when it is auto-opened by ?quick=1.
  const [formTouched, setFormTouched] = useState(false);
  const [venueTouched, setVenueTouched] = useState(false);

  // Reset the draft every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setFormTouched(false);
    setVenueTouched(false);
    setSaving(false);
    setNewVenueName('');
    if (orgSlug) getLocationsByLeagueName(orgSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open || formTouched) return;
    const next = defaultQuickAvailability(timelessGames, { today: formatDateKey(new Date()), gameDurationInMinutes });
    setForm(next);
    setDuration(String(next.gameDurationInMinutes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formTouched, timelessGames.length, timelessGames[0]?.dateKey, timelessGames[timelessGames.length - 1]?.dateKey, gameDurationInMinutes]);

  const orgLocations = useMemo(
    () => (locations || []).filter((l: any) => !orgId || helpers.getObjId(l.organization) === orgId || !l.organization),
    [locations, orgId],
  );
  useEffect(() => {
    if (!open) return;
    const stillValid = locationId === NEW_VENUE || orgLocations.some((l: any) => l._id === locationId);
    if (!venueTouched || !locationId || !stillValid) {
      setLocationId(orgLocations[0]?._id || NEW_VENUE);
    }
  }, [open, orgLocations, locationId, venueTouched]);
  useEffect(() => {
    if (!open) return;
    const fixed = competition?.id || competitions[0]?.id || '';
    setCompetitionId((prev) => (prev && (competition ? prev === competition.id : competitions.some((c) => c.id === prev)) ? prev : fixed));
  }, [open, competition, competitions]);

  const activeCompetition: WizardCompetition | null =
    competition || competitions.find((c) => c.id === competitionId) || null;

  const effective: QuickAvailabilityInput = { ...form, gameDurationInMinutes: Number(duration) || 0 };
  const preview = useMemo(() => {
    const slots = buildQuickSlots(effective);
    const plan = planGamePlacement(timelessGames, effective);
    return { slots: slots.length, perDay: slots[0]?.maxGames || 0, placed: plan.placed.length, unplaced: plan.unplaced.length, lastDateKey: plan.lastDateKey };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective.daysOfWeek.join(','), effective.startTime, effective.endTime, effective.gameDurationInMinutes, effective.dateFrom, effective.dateTo, timelessGames]);

  const touch = () => setFormTouched(true);
  const toggleDay = (n: number) => { touch(); setForm((f) => ({
    ...f,
    daysOfWeek: f.daysOfWeek.includes(n) ? f.daysOfWeek.filter((d) => d !== n) : [...f.daysOfWeek, n].sort((a, b) => a - b),
  })); };

  const canCreate = !!orgId && !!activeCompetition && preview.slots > 0 && !saving
    && (locationId !== NEW_VENUE || newVenueName.trim().length > 0);

  const create = async () => {
    if (!canCreate || !activeCompetition) return;
    setSaving(true);
    const payload: any = {
      organization: orgId,
      [activeCompetition.kind]: activeCompetition.id,
      daysOfWeek: effective.daysOfWeek,
      startTime: effective.startTime,
      endTime: effective.endTime,
      gameDurationInMinutes: effective.gameDurationInMinutes,
      dateFrom: effective.dateFrom,
      dateTo: effective.dateTo,
      ...(locationId === NEW_VENUE ? { newLocationName: newVenueName.trim() } : { location: locationId }),
    };
    const { error, result } = await quickAvailability(payload);
    setSaving(false);
    if (error || !result) {
      toast.error(error || 'Could not set up availability.');
      return;
    }
    const r = result as QuickAvailabilityResult;
    const parts = [`${r.slotsCreated} day${r.slotsCreated === 1 ? '' : 's'} of availability added`];
    if (r.gamesPlaced) parts.push(`${r.gamesPlaced} game${r.gamesPlaced === 1 ? '' : 's'} got a time`);
    if (r.gamesUnplaced) parts.push(`${r.gamesUnplaced} still need one (widen the window)`);
    toast.success(parts.join('. ') + '.');
    onOpenChange(false);
    onDone?.(r);
  };

  const gamesWord = timelessGames.length === 1 ? 'game' : 'games';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-tour="quick-availability-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            {timelessGames.length ? 'Set game times' : `Set up ${venuesLabel.toLowerCase()} availability`}
          </DialogTitle>
          <DialogDescription>
            {timelessGames.length
              ? `Pick when you play and ${timelessGames.length} drafted ${gamesWord} get real times. The day grid, drag and drop, and conflict checks turn on too.`
              : 'Pick when you play. The day grid, drag and drop, Auto-Populate, and conflict checks turn on.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!competition && competitions.length > 0 && (
            <div className="space-y-1">
              <Label className="text-sm">Competition</Label>
              <Select value={competitionId} onValueChange={setCompetitionId}>
                <SelectTrigger aria-label="Competition"><SelectValue placeholder="Pick a league or tournament" /></SelectTrigger>
                <SelectContent>
                  {competitions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-sm">{venueSingular}</Label>
            <Select value={locationId} onValueChange={(v) => { setVenueTouched(true); setLocationId(v); }}>
              <SelectTrigger aria-label={venueSingular}><SelectValue placeholder={`Pick a ${venueSingular.toLowerCase()}`} /></SelectTrigger>
              <SelectContent>
                {orgLocations.map((l: any) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                <SelectItem value={NEW_VENUE}>New {venueSingular.toLowerCase()}…</SelectItem>
              </SelectContent>
            </Select>
            {locationId === NEW_VENUE && (
              <Input
                aria-label={`New ${venueSingular.toLowerCase()} name`}
                placeholder={`${venueSingular} name`}
                value={newVenueName}
                onChange={(e) => setNewVenueName(e.target.value)}
                data-tour="quick-availability-new-venue"
              />
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-sm">Game days</Label>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Game days">
              {DAYS.map((d) => (
                <Button
                  key={d.n}
                  type="button"
                  size="sm"
                  variant={form.daysOfWeek.includes(d.n) ? 'default' : 'outline'}
                  aria-pressed={form.daysOfWeek.includes(d.n)}
                  onClick={() => toggleDay(d.n)}
                  data-tour={`quick-availability-day-${d.n}`}
                >
                  {d.short}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-sm">First game</Label>
              <Input type="time" aria-label="First game time" value={form.startTime} onChange={(e) => { touch(); setForm((f) => ({ ...f, startTime: e.target.value })); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Last game ends</Label>
              <Input type="time" aria-label="Last game ends" value={form.endTime} onChange={(e) => { touch(); setForm((f) => ({ ...f, endTime: e.target.value })); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Game length (min)</Label>
              <Input
                type="text" inputMode="numeric" aria-label="Game length in minutes"
                value={duration}
                onChange={(e) => { touch(); setDuration(e.target.value.replace(/[^0-9]/g, '')); }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-sm">From</Label>
              <Input type="date" aria-label="From date" value={form.dateFrom} onChange={(e) => { touch(); setForm((f) => ({ ...f, dateFrom: e.target.value })); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Through</Label>
              <Input type="date" aria-label="Through date" value={form.dateTo} onChange={(e) => { touch(); setForm((f) => ({ ...f, dateTo: e.target.value })); }} />
            </div>
          </div>

          <p className="text-sm text-muted-foreground" data-tour="quick-availability-preview">
            {preview.slots === 0
              ? 'That window has no game slots yet. Check the days, times, and dates.'
              : (
                <>
                  {preview.slots} game day{preview.slots === 1 ? '' : 's'}, {preview.perDay} game{preview.perDay === 1 ? '' : 's'} per day
                  {timelessGames.length > 0 && (
                    <>
                      . {preview.placed} of {timelessGames.length} drafted {gamesWord} get a time
                      {preview.lastDateKey ? `, last on ${preview.lastDateKey}` : ''}
                      {preview.unplaced > 0 ? `. ${preview.unplaced} will not fit: widen the window or add days` : ''}
                    </>
                  )}
                  .
                </>
              )}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={create} disabled={!canCreate} data-tour="quick-availability-create">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-2 h-4 w-4" />}
            {timelessGames.length ? 'Set times' : 'Create availability'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
